import React, { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { FaPlay, FaBackward, FaForward, FaPause } from 'react-icons/fa';
import { useAlertDialog } from '../contexts/AlertDialogContext'; // Import the hook
import './Login.css';

// URL de l'API YouTube
const youTubeScriptUrl = "https://www.youtube.com/iframe_api";

function ContactForm() {
  const [name, setName] = useState('');
  const [firstName, setFirstName] = useState('');
  const [email, setEmail] = useState('');
  const [pays, setPays] = useState('');
  const [ville, setVille] = useState('');
  const [phoneNumber, setPhoneNumber] = useState('');
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { openAlertDialog } = useAlertDialog(); // Get the function from the context

  const [playing, setPlaying] = useState(false);
  const iframeRef = useRef(null);
  const playerRef = useRef(null);
  const [isYoutubeApiLoaded, setIsYoutubeApiLoaded] = useState(false);

  // Map des IDs de vidéos YouTube par langue
  const videoMap = {
      fr: "Pm_PkKyjD4k",
      en: "8taKZRtAzr4",
  };

  // Charge le script de l'API YouTube
  useEffect(() => {
    if (!window.YT) {
      const tag = document.createElement("script");
      tag.src = youTubeScriptUrl;
      const firstScriptTag = document.getElementsByTagName("script")[0];
      firstScriptTag.parentNode.insertBefore(tag, firstScriptTag);

      window.onYouTubeIframeAPIReady = () => {
        setIsYoutubeApiLoaded(true);
      };
    } else {
      setIsYoutubeApiLoaded(true);
    }
  }, []);

  // Initialise ou met à jour le lecteur vidéo
  useEffect(() => {
    if (isYoutubeApiLoaded && iframeRef.current) {
      const videoId = videoMap[i18n.language] || videoMap.fr;

      if (playerRef.current) {
        playerRef.current.loadVideoById(videoId);
      } else {
        playerRef.current = new window.YT.Player(iframeRef.current, {
          videoId: videoId,
          events: {
            onReady: onPlayerReady,
            onStateChange: onPlayerStateChange,
          },
          playerVars: {
            controls: 0,
            modestbranding: 1,
            rel: 0,
            autoplay: 0,
            showinfo: 0,
            disablekb: 1,
          },
        });
      }
      setPlaying(false);
    }

    return () => {
      if (playerRef.current) {
        playerRef.current.destroy();
        playerRef.current = null;
      }
    };
  }, [i18n.language, isYoutubeApiLoaded]);

  const onPlayerReady = (event) => {
    console.log("YouTube Player est prêt !", event);
  };

  const onPlayerStateChange = (event) => {
    if (event.data === window.YT.PlayerState.PLAYING) {
      setPlaying(true);
    } else {
      setPlaying(false);
    }
  };

  const handlePlayPause = () => {
    if (playerRef.current) {
      if (playing) {
        playerRef.current.pauseVideo();
      } else {
        playerRef.current.playVideo();
      }
    }
  };

  const handleRewind = () => {
    if (playerRef.current) {
      const currentTime = playerRef.current.getCurrentTime();
      playerRef.current.seekTo(currentTime - 10, true);
    }
  };

  const handleFastForward = () => {
    if (playerRef.current) {
      const currentTime = playerRef.current.getCurrentTime();
      playerRef.current.seekTo(currentTime + 10, true);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Lire le lien_id depuis les paramètres de l'URL
    const queryParams = new URLSearchParams(window.location.search);
    const lien_id = queryParams.get('lien_id');

    // Vérifier si le prospect existe déjà
    try {
        const checkResponse = await fetch(`https://back-desalpe.onrender.com/api/inscription/prospects/check-exists?email=${email}`);
        const checkData = await checkResponse.json();

        if (checkData.exists) {
            // Use the modal instead of alert
            openAlertDialog("Merci, mais vous êtes déjà participant / Thank you but you are already a participant", "Information");
            return; // Empêcher la soumission du formulaire
        }
    } catch (error) {
        console.error('Error checking prospect existence:', error);
        // Use the modal for errors as well
        openAlertDialog('Une erreur est survenue lors de la vérification de votre statut. Veuillez réessayer.', 'Erreur');
        return; // Empêcher la soumission du formulaire en cas d'erreur de vérification
    }

    const prospectData = {
        firstName: firstName,
        name: name, // Corresponds to last_name
        email: email,
        pays: pays,
        ville: ville,
        phoneNumber: phoneNumber
    };

    try {
        const response = await fetch('https://back-desalpe.onrender.com/api/inscription/prospects', {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
            },
            body: JSON.stringify(prospectData),
        });

        if (!response.ok) {
            const errorData = await response.json();
            throw new Error(errorData.message || 'Failed to submit form.');
        }

        // Enregistre le clic de soumission de formulaire avec succès
        fetch('https://back-desalpe.onrender.com/api/click-events', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ 
                click_type: 'FORMULAIRE_SOUMIS',
                lien_id: lien_id // Envoyer l'ID du lien
            }),
        }).catch(error => console.error("Erreur lors de l'enregistrement du clic de formulaire:", error));

        // On success, navigate to the next page
        navigate('/selection');

    } catch (error) {
        console.error('Form submission error:', error);
        openAlertDialog(`Erreur lors de la soumission : ${error.message}`, 'Erreur');
    }
  };

  return (
      <div className="login-container">
          <h1>{t("contactForm.step1_title")}</h1>
          <p className="form-description">
              {t("contactForm.description_text")}
          </p>
          <div className="contact-video-player-area">
              <div className="contact-player-wrapper">
                  <div className="video-overlay"></div>
                  <div id="contact-youtube-player" ref={iframeRef}></div>
              </div>
              <p className="contact-video-instruction">
                  {t("invitation.click_to_play")}
              </p>
              <div className="video-controls">
                  <button
                      onClick={handleRewind}
                      className="video-control-button"
                  >
                      <FaBackward />
                  </button>
                  <button
                      onClick={handlePlayPause}
                      className="play-pause-button"
                  >
                      {playing ? <FaPause /> : <FaPlay />}
                  </button>
                  <button
                      onClick={handleFastForward}
                      className="video-control-button"
                  >
                      <FaForward />
                  </button>
              </div>
          </div>
          <p className="form-description">
              {t("contactForm.description_text1")}
          </p>
          <form onSubmit={handleSubmit} className="login-form">
              <div className="form-group">
                  <input
                      type="text"
                      id="firstName"
                      value={firstName}
                      onChange={(e) => setFirstName(e.target.value)}
                      placeholder={t("contactForm.firstName_label")}
                      required
                  />
              </div>

              <div className="form-group">
                  <input
                      type="text"
                      id="name"
                      value={name}
                      onChange={(e) => setName(e.target.value)}
                      placeholder={t("contactForm.name_label")}
                      required
                  />
              </div>
              <div className="form-group">
                  <input
                      type="email"
                      id="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      placeholder={t("contactForm.email_label")}
                      required
                  />
              </div>
              <div className="form-group">
                  <input
                      type="text"
                      id="pays"
                      value={pays}
                      onChange={(e) => setPays(e.target.value)}
                      placeholder={t("contactForm.pays_label")}
                      required
                  />
              </div>
              <div className="form-group">
                  <input
                      type="text"
                      id="ville"
                      value={ville}
                      onChange={(e) => setVille(e.target.value)}
                      placeholder={t("contactForm.ville_label")}
                      required
                  />
              </div>

              <div className="form-group">
                  <input
                      type="tel"
                      id="phoneNumber"
                      value={phoneNumber}
                      onChange={(e) => setPhoneNumber(e.target.value)}
                      placeholder={t("contactForm.phoneNumber_label")}
                  />
              </div>

              <button type="submit" className="login-submit-btn">
                  {t("contactForm.submit_button")}
              </button>
          </form>
      </div>
  );
}

export default ContactForm;